import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import {
  GoogleMap,
  DirectionsRenderer,
  OverlayView,
  Polyline,
  Marker,
} from "@react-google-maps/api";

// Chromium 63-safe — no ?. / flex gap
const legEnd = function (r) {
  if (!r || !r.directions || !r.directions.routes) return null;
  var idx = r.routeIndex || 0;
  var route = r.directions.routes[idx];
  if (!route || !route.legs || !route.legs[0]) return null;
  return route.legs[0].end_location;
};

const ALT_COLOR = "#6978ea";
const PURPLE = "#6C4DF6";

const VEHICLES = ["🚗", "🚌", "🏍️"];

const RouteMap = function (props) {
  var s = props.s;
  var pickup = props.pickup;
  var routes = props.routes;
  var single = props.single;
  var onMapLoad = props.onMapLoad;

  var showAlts = false;
  var traffic =
    single && routes[0] && routes[0].trafficRoute
      ? routes[0].trafficRoute
      : null;
  var hasTraffic =
    !!(traffic && traffic.segments && traffic.segments.length);
  var baseLine = null;
  if (single && routes[0] && routes[0].alts) {
    for (var ai = 0; ai < routes[0].alts.length; ai++) {
      if (routes[0].alts[ai].isFastest) {
        baseLine = routes[0].alts[ai].path;
        break;
      }
    }
  }

  var _m = useState(null);
  var mapObj = _m[0];
  var setMapObj = _m[1];
  var _o = useState({});
  var offsets = _o[0];
  var setOffsets = _o[1];
  var badgeRefs = useRef({});
  var offsetsRef = useRef({});

  var layoutBadges = useCallback(
    function () {
      var alts = [];
      if (single && routes[0] && routes[0].alts) {
        alts = routes[0].alts.filter(function (a) {
          return a.isFastest;
        });
      }
      if (!mapObj || alts.length === 0 || !window.google) return;
      var proj = mapObj.getProjection();
      var bounds = mapObj.getBounds();
      if (!proj || !bounds) return;

      var scale = Math.pow(2, mapObj.getZoom());
      var bl = proj.fromLatLngToPoint(bounds.getSouthWest());
      var tr = proj.fromLatLngToPoint(bounds.getNorthEast());
      var toPixel = function (lat, lng) {
        var wp = proj.fromLatLngToPoint(
          new window.google.maps.LatLng(lat, lng)
        );
        return { x: (wp.x - bl.x) * scale, y: (wp.y - tr.y) * scale };
      };

      var linePts = [];
      alts.forEach(function (a) {
        (a.path || []).forEach(function (p) {
          linePts.push(toPixel(p.lat(), p.lng()));
        });
      });

      var gap = 10;
      var dirs = [
        [1, 0],
        [1, -0.7],
        [1, 0.7],
        [0, -1],
        [0, 1],
        [-1, -0.7],
        [-1, 0.7],
        [-1, 0],
      ];

      var boxHitsBox = function (a, b) {
        return (
          a.left < b.left + b.w &&
          a.left + a.w > b.left &&
          a.top < b.top + b.h &&
          a.top + a.h > b.top
        );
      };

      var mapW = (tr.x - bl.x) * scale;
      var mapH = (bl.y - tr.y) * scale;
      var inBounds = function (box) {
        return (
          box.left >= 0 &&
          box.top >= 0 &&
          box.left + box.w <= mapW &&
          box.top + box.h <= mapH
        );
      };

      var minLineDist = function (box) {
        var min = Infinity;
        for (var pi = 0; pi < linePts.length; pi++) {
          var p = linePts[pi];
          var dx = Math.max(box.left - p.x, 0, p.x - (box.left + box.w));
          var dy = Math.max(box.top - p.y, 0, p.y - (box.top + box.h));
          var d = Math.sqrt(dx * dx + dy * dy);
          if (d < min) min = d;
        }
        return min;
      };

      var placed = [];
      var next = {};
      alts.forEach(function (alt) {
        var px = toPixel(alt.lat, alt.lng);
        var el = badgeRefs.current[alt.routeIndex];
        var w = el ? el.offsetWidth : 90;
        var h = el ? el.offsetHeight : 44;

        var best = null;
        var bestClear = -Infinity;
        for (var r = 1; r <= 12; r++) {
          var radius = gap + r * 0.5 * h;
          var clearFound = false;
          for (var di = 0; di < dirs.length; di++) {
            var dx = dirs[di][0];
            var dy = dirs[di][1];
            var cx = px.x + dx * radius;
            var cy = px.y + dy * radius;
            var box = { left: cx - w / 2, top: cy - h / 2, w: w, h: h };
            if (!inBounds(box)) continue;
            var hit = false;
            for (var bi = 0; bi < placed.length; bi++) {
              if (boxHitsBox(box, placed[bi])) {
                hit = true;
                break;
              }
            }
            if (hit) continue;
            var dist = minLineDist(box);
            if (dist > bestClear) {
              bestClear = dist;
              best = box;
            }
            if (dist >= 2) {
              clearFound = true;
              break;
            }
          }
          if (clearFound) break;
        }

        if (!best) best = { left: px.x + gap, top: px.y - h / 2, w: w, h: h };

        placed.push(best);

        var bcx = best.left + w / 2;
        var bcy = best.top + h / 2;
        var adx = px.x - bcx;
        var ady = px.y - bcy;
        var tail =
          Math.abs(adx) >= Math.abs(ady)
            ? adx > 0
              ? "right"
              : "left"
            : ady > 0
            ? "bottom"
            : "top";
        next[alt.routeIndex] = {
          x: Math.round(best.left - px.x),
          y: Math.round(best.top - px.y),
          tail: tail,
        };
      });

      var prev = offsetsRef.current;
      var nKeys = Object.keys(next);
      var pKeys = Object.keys(prev);
      var same =
        nKeys.length === pKeys.length &&
        nKeys.every(function (k) {
          return (
            prev[k] &&
            prev[k].x === next[k].x &&
            prev[k].y === next[k].y
          );
        });
      if (!same) {
        offsetsRef.current = next;
        setOffsets(next);
      }
    },
    [mapObj, routes, single]
  );

  useEffect(
    function () {
      if (!mapObj || !window.google) return;
      var l = window.google.maps.event.addListener(
        mapObj,
        "idle",
        layoutBadges
      );
      layoutBadges();
      return function () {
        window.google.maps.event.removeListener(l);
      };
    },
    [mapObj, layoutBadges]
  );

  var t = parseFloat(s.carIcon.width) * 0.55;
  var tailStyle = function (side) {
    var c = "#ffffff";
    var base = { position: "absolute", width: 0, height: 0 };
    var clear = t + "px solid transparent";
    if (side === "left")
      return Object.assign({}, base, {
        left: -t,
        top: "calc(50% - " + t + "px)",
        borderTop: clear,
        borderBottom: clear,
        borderRight: t + "px solid " + c,
      });
    if (side === "right")
      return Object.assign({}, base, {
        right: -t,
        top: "calc(50% - " + t + "px)",
        borderTop: clear,
        borderBottom: clear,
        borderLeft: t + "px solid " + c,
      });
    if (side === "top")
      return Object.assign({}, base, {
        top: -t,
        left: "calc(50% - " + t + "px)",
        borderLeft: clear,
        borderRight: clear,
        borderBottom: t + "px solid " + c,
      });
    return Object.assign({}, base, {
      bottom: -t,
      left: "calc(50% - " + t + "px)",
      borderLeft: clear,
      borderRight: clear,
      borderTop: t + "px solid " + c,
    });
  };

  var r0 = routes[0];
  var altsLen =
    r0 && r0.alts && r0.alts.length ? r0.alts.length : 0;

  return (
    <GoogleMap
      mapContainerStyle={s.mapContainer}
      center={pickup.location}
      zoom={12}
      options={s.mapOptions}
      onLoad={function (m) {
        setMapObj(m);
        if (onMapLoad) onMapLoad(m);
      }}
    >
      {showAlts &&
        r0 &&
        r0.alts &&
        r0.alts
          .filter(function (alt) {
            return !alt.isFastest;
          })
          .map(function (alt) {
            return (
              <Polyline
                key={alt.routeIndex}
                path={alt.path}
                options={{
                  strokeColor: ALT_COLOR,
                  strokeOpacity: 0.9,
                  strokeWeight: s.routePolyline.strokeWeight * 0.8,
                  zIndex: 1,
                }}
              />
            );
          })}

      {single && r0 ? (
        <Fragment>
          {r0.directions ? (
            <DirectionsRenderer
              directions={r0.directions}
              routeIndex={r0.routeIndex || 0}
              options={{
                preserveViewport: true,
                suppressMarkers: true,
                polylineOptions: Object.assign({}, s.routePolyline, {
                  strokeColor: r0.color,
                  strokeOpacity: baseLine ? 0 : 1,
                  zIndex: 10,
                }),
              }}
            />
          ) : null}
          {baseLine ? (
            <Polyline
              path={baseLine}
              options={Object.assign({}, s.routePolyline, {
                strokeColor: r0.color,
                strokeOpacity: 1,
                zIndex: 10,
              })}
            />
          ) : null}
          {hasTraffic
            ? traffic.segments.map(function (seg, i) {
                return (
                  <Polyline
                    key={"traffic-" + i}
                    path={seg.path}
                    options={{
                      strokeColor: seg.color,
                      strokeOpacity: 1,
                      strokeWeight: s.routePolyline.strokeWeight,
                      zIndex: 20,
                    }}
                  />
                );
              })
            : null}
        </Fragment>
      ) : (
        <Fragment>
          {routes.map(function (r) {
            if (!r.directions) return null;
            return (
              <DirectionsRenderer
                key={r.index}
                directions={r.directions}
                routeIndex={r.routeIndex || 0}
                options={{
                  preserveViewport: true,
                  suppressMarkers: true,
                  polylineOptions: Object.assign({}, s.routePolyline, {
                    strokeColor: PURPLE,
                  }),
                }}
              />
            );
          })}
          {routes.map(function (r) {
            var segs =
              r.trafficRoute && r.trafficRoute.segments
                ? r.trafficRoute.segments
                : null;
            if (!segs) return null;
            return segs.map(function (seg, i) {
              return (
                <Polyline
                  key={"traffic-" + r.index + "-" + i}
                  path={seg.path}
                  options={{
                    strokeColor: seg.color,
                    strokeOpacity: 1,
                    strokeWeight: s.routePolyline.strokeWeight,
                    zIndex: 20,
                  }}
                />
              );
            });
          })}
        </Fragment>
      )}

      {pickup && pickup.location ? (
        <Marker position={pickup.location} label="A" />
      ) : null}
      {single
        ? r0 &&
          legEnd(r0) && <Marker position={legEnd(r0)} label="B" />
        : routes.map(function (r) {
            var end = legEnd(r);
            return end ? (
              <Marker
                key={"pin-" + r.index}
                position={end}
                label={String.fromCharCode(66 + r.index)}
              />
            ) : null;
          })}

      {single &&
        altsLen > 0 &&
        r0.alts
          .filter(function (alt) {
            return alt.isFastest;
          })
          .map(function (alt) {
            var off = offsets[alt.routeIndex];
            return (
              <OverlayView
                key={"badge-" + alt.routeIndex}
                position={{ lat: alt.lat, lng: alt.lng }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={function (w, h) {
                  return off || { x: 8, y: -(h / 2) };
                }}
              >
                <div
                  ref={function (el) {
                    if (el) badgeRefs.current[alt.routeIndex] = el;
                  }}
                  style={Object.assign(
                    {},
                    alt.isFastest ? s.carBadgeFast : s.carBadge,
                    { position: "relative" }
                  )}
                >
                  {off && off.tail ? (
                    <span style={tailStyle(off.tail)} />
                  ) : null}
                  <span
                    style={Object.assign({}, s.carIcon, {
                      fontSize: s.carIcon.width,
                      lineHeight: 1,
                      width: "auto",
                      height: "auto",
                    })}
                    aria-hidden="true"
                  >
                    {VEHICLES[alt.routeIndex % VEHICLES.length]}
                  </span>
                  <span style={s.carBadgeText}>
                    <span style={s.carBadgeTime}>{alt.duration}</span>
                    <span style={s.carBadgeDist}>{alt.distance}</span>
                  </span>
                </div>
              </OverlayView>
            );
          })}
    </GoogleMap>
  );
};

export default RouteMap;
